import { initializeApp } from 'firebase/app';
import { getToken, type MessagePayload } from 'firebase/messaging';
import { UserService } from '.';
import { LocalStorageUtils } from '@/utils';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

/**
 * Messaging needs these four; the rest of the config is optional at init time.
 *
 * `initializeApp` accepts a config with holes and defers the complaint, so the failure surfaces much
 * later as `installations/missing-app-config-values` thrown from inside `getMessaging()` — an
 * unhandled rejection, which the global handler installed in `main.ts` turns into a full error screen.
 * A workspace whose `.env.local` was rewritten without the VITE_FIREBASE_* keys should lose push
 * notifications, not the app.
 */
const requiredConfig = ['apiKey', 'projectId', 'messagingSenderId', 'appId'] as const;

const missingConfig = requiredConfig.filter(key => !firebaseConfig[key]);

const app = missingConfig.length > 0 ? null : initializeApp(firebaseConfig);

if (missingConfig.length > 0) {
  /* eslint-disable-next-line no-console */
  console.warn(
    '[firebase] push notifications disabled — missing env: ' +
      missingConfig.map(key => `VITE_FIREBASE_${key.replace(/([A-Z])/g, '_$1').toUpperCase()}`).join(', ')
  );
}

/** Where `push-inbox` serves its SSE stream. Dev-only, so a plain default is enough. */
const PUSH_INBOX_URL = import.meta.env.VITE_PUSH_INBOX_URL ?? 'http://localhost:8026';

/** Every local-push line is tagged, so `[push-inbox]` in the console filter shows the whole path. */
const SW_READY_TIMEOUT_MS = 5000;

const pushLog = (...args: unknown[]) => {
  /* eslint-disable-next-line no-console */
  console.info('[push-inbox]', ...args);
};

const isWebView = () => {
  const ua = navigator.userAgent || '';
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return Boolean((window as any).ReactNativeWebView) ||
    /wv|WebView/i.test(ua) ||
    (/iPhone|iPad|iPod/i.test(ua) && !/Safari/i.test(ua));
};

const canUseMessaging = async () => {
  if (!app) return false;
  if (typeof window === 'undefined') return false;
  if (isWebView()) return false;
  if (!('serviceWorker' in navigator)) return false;

  const { isSupported } = await import('firebase/messaging');
  return await isSupported();
};

/**
 * Never throws: every caller treats a null messaging instance as "notifications are off", and a
 * rejection here would escape as an unhandled promise from `onMounted`.
 */
const getMessagingSafe = async () => {
  try {
    if (!(await canUseMessaging())) return null;
    const { getMessaging } = await import('firebase/messaging');
    return getMessaging(app!);
  } catch (error) {
    /* eslint-disable-next-line no-console */
    console.warn('[firebase] messaging unavailable', error);
    return null;
  }
};

export const requestForToken = async () => {
  const messaging = await getMessagingSafe();
  if (!messaging) return;

  return getToken(messaging, { vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY })
    .then(async currentToken => {
      if (currentToken) {
        if (LocalStorageUtils.getFcmToken() !== currentToken) {
          // Send the token to your server and update the UI if necessary
          const userId = LocalStorageUtils.getUserId();
          const status = await UserService.registerFCMToken(currentToken, userId);
          if (status) {
            LocalStorageUtils.saveFcmToken(currentToken);
          } else {
            // eslint-disable-next-line no-console
            console.log('Failed to register token with server');
          }
        } else {
          // eslint-disable-next-line no-console
          console.log('Current token already registered');
        }
      } else {
        // Show permission request UI
        // eslint-disable-next-line no-console
        console.log('No registration token available. Request permission to generate one.');
      }
    })
    .catch(err => {
      // eslint-disable-next-line no-console
      console.log('An error occurred while retrieving token. ', err);
    });
};

/**
 * The worker that renders background notifications, registering it if needed.
 *
 * FCM registers this same file from `getToken()`, but that only runs once the data section mounts —
 * and unregistering the worker to pick up an edit is routine. Registering it here keeps the local
 * bridge working either way. Resolves to null rather than pending if activation stalls.
 */
const activeWorker = async (): Promise<ServiceWorker | null> => {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing?.active) return existing.active;

  try {
    await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  } catch (error) {
    pushLog('could not register the notification worker', error);
    return null;
  }

  const ready = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>(resolve => setTimeout(() => resolve(null), SW_READY_TIMEOUT_MS))
  ]);

  if (!ready) pushLog('the notification worker did not activate in time');
  return ready?.active ?? null;
};

/**
 * Local development only: bridge `push-inbox` to the real notification paths.
 *
 * LocalStack's SNS retains what the API publishes but never calls FCM, so no push ever reaches the
 * browser and both `onMessage` and the worker's `onBackgroundMessage` stay silent. `push-inbox`
 * (:8026) re-serves those messages over SSE; this reshapes each one back into FCM's envelope and
 * routes it the way FCM would — a toast while the tab is visible, a service-worker notification
 * while it is not — so neither path is special-cased for local runs.
 *
 * Deliberately unfiltered. `/api/stream` can narrow by `?token=`, but an FCM token refreshes while
 * old device rows survive, so the API often publishes to an endpoint whose token no longer matches
 * the one in localStorage — and a filtered stream then drops every push with no way to tell. A local
 * stack serves one developer, so taking all of them and logging the endpoint is both simpler and
 * honest about what arrived.
 *
 * Resolves to an unsubscribe function, a no-op one outside dev. Never rejects.
 */
export const subscribeLocalPush = async (onForeground: (payload: MessagePayload) => void) => {
  const noop = () => {};

  if (!import.meta.env.DEV) return noop;
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    pushLog('no service worker in this browser — local pushes disabled');
    return noop;
  }

  try {
    const url = `${PUSH_INBOX_URL}/api/stream`;
    pushLog(`connecting to ${url}`);

    // Deliberately not awaiting `navigator.serviceWorker.ready` here. It resolves only once a
    // worker is active for this scope and otherwise pends forever — never rejecting, never timing
    // out — so a session that has not reached the data section yet (which is what calls
    // `requestForToken`, and so what registers the worker) would hang before the stream was even
    // opened. The foreground toast needs no worker at all; the background path resolves one lazily.
    const source = new EventSource(url);

    source.onopen = () => pushLog('connected — pushes published by the API will arrive here');
    source.onerror = () =>
      pushLog(`stream error — is push-inbox reachable at ${PUSH_INBOX_URL}? (docker compose ps push-inbox)`);

    source.onmessage = event => {
      // Guarded as a whole, not just the JSON.parse: this is a dev-only convenience, and a throw from
      // an event callback escapes the enclosing try/catch entirely — it would surface as an uncaught
      // error and raise the very error screen the bridge exists to help debug.
      try {
        const message = JSON.parse(event.data);
        const from = message.endpoint?.split('/').pop();

        // `updateBadge` in the API publishes `{notification:{badge:n}}` — no title, no body. That is
        // an app icon count for the mobile clients, not something to show, and coercing its missing
        // fields into empty strings is what made it render as a blank notification.
        if (message.title == null && message.body == null) {
          pushLog(`badge update (${message.badge}) from ${from} — nothing to render`);
          return;
        }

        const payload = {
          notification: { title: message.title ?? '', body: message.body ?? '' },
          data: message.data ?? {}
        } as MessagePayload;

        // The tab being visible is what FCM uses to pick between onMessage and onBackgroundMessage.
        const visible = document.visibilityState === 'visible';
        pushLog(
          `push "${message.title ?? ''}" from ${from} -> ` +
            (visible ? 'toast (foreground)' : 'service worker (background)'),
          message
        );

        if (visible) {
          onForeground(payload);
          return;
        }

        void activeWorker().then(worker => {
          if (worker) {
            worker.postMessage({ type: 'local-push', payload });
          } else {
            pushLog('no active service worker, so the background notification cannot be shown');
          }
        });
      } catch (error) {
        pushLog('ignored a malformed local push message', error);
      }
    };

    return () => source.close();
  } catch (error) {
    pushLog('local push bridge unavailable', error);
    return noop;
  }
};

/** Resolves to an unsubscribe function, a no-op one when messaging is off. Never rejects. */
export const onMessageListener = async (callback: (payload: MessagePayload) => void) => {
  const messaging = await getMessagingSafe();
  if (!messaging) return () => {};

  try {
    const { onMessage } = await import('firebase/messaging');
    return onMessage(messaging, payload => callback(payload));
  } catch (error) {
    /* eslint-disable-next-line no-console */
    console.warn('[firebase] could not subscribe to foreground messages', error);
    return () => {};
  }
};
